import {
  Autorenew,
  Block,
  Circle,
  Download,
  Info,
  Receipt,
} from '@mui/icons-material';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { getInvoicingBookReport } from 'lib/api/reports';
import { getDocumentStatus, refillInvoicingInformation } from 'lib/api/sii';
import { getSelectedCertificate } from 'lib/api/SIICertificates';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import TheBillerFinalizedModal from '../../../SII/components/TheBiller/components/finalizedModal';
import DteStatusDialog from './components/DteStatusDialog';
import ReportInvoicingConfirmDialog from './components/Invoicing/confirmDialog';
import SubjectOptionsDialog from './components/Invoicing/SubjectOptionsDialog';
import MonthYearSearcher from './components/MonthYearSearcher';
import NullmentConfirmDialog from './components/Nullment/NullmentConfirmDialog';
import NullmentGlossDialog from './components/Nullment/NullmentGlossDialog';

dayjs.locale('es');

const InvoicingBook: React.FC = () => {
  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [selected, setSelected] = useState([]);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  const [loading, setLoading] = useState(true);
  const [successInvoices, setSuccessInvoices] = useState<any>([]);
  const [resultInvoices, setResultInvoices] = useState<any>([]);
  const [finalizedModal, setFinalizedModal] = useState(false);
  const [dteStatusDialogOpen, setDteStatusDialogOpen] = useState(false);
  const [dteStatus, setDteStatus] = useState<any>(null);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [alreadyInvoicedInvoices, setAlreadyInvoicedInvoices] = useState<any>(
    [],
  );
  const [invoicingPartner, setInvoicingPartner] = useState<any>(null);
  const dispatch = useDispatch();
  const [type, setType] = useState<'Por facturar' | 'Facturado'>(
    'Por facturar',
  );
  // Nullment states
  const [nullmentGlossDialogOpen, setNullmentGlossDialogOpen] = useState(false);
  const [nullmentConfirmDialogOpen, setNullmentConfirmDialogOpen] =
    useState(false);
  const [nullmentGloss, setNullmentGloss] = useState('');
  const [selectedNullmentInvoices, setSelectedNullmentInvoices] = useState<any>(
    [],
  );
  const [selectedNullmentIds, setSelectedNullmentIds] = useState<any>([]);
  const [refillLoading, setRefillLoading] = useState(false);

  const handleClose = () => {
    setFinalizedModal(false);
  };

  const exportToExcel = async () => {
    const selectedInvoices = data.filter((item: any) =>
      selectedIds.includes(item.id),
    );
    const plainData: any = [];
    selectedInvoices.forEach((item: any) => {
      plainData.push([
        'FACTURA',
        item.businessIdentifierRv,
        item.businessIdentifierDv,
        item.businessName,
        item.index,
        item.invoicingDate,
        item.invoicingDate,
        item.totalCosts,
        item.totalCostsIva,
        item.totalCostsWithIva,
        '1',
        item.commune,
        item.address,
        item.email,
        'Sin informacion',
      ]);
      plainData.push([
        'DETALLE',
        item.index,
        'Servicios Factoring',
        '1',
        '1',
        item.totalCosts,
        item.totalCosts,
      ]);
    });
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Add data to worksheet
    [...plainData].forEach((rowData) => {
      worksheet.addRow(rowData);
    });
    // Save workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.xlsx';
    a.click();

    URL.revokeObjectURL(url);
  };

  const setUp = async () => {
    setLoading(true);
    const start = dayjs(new Date(year, month, 1)).startOf('month');
    const end = dayjs(new Date(year, month, 1)).endOf('month');
    setStartDate(start);
    setEndDate(end);
    try {
      const {
        completeInvoices,
        alreadyInvoicedInvoices,
        system_invoicing_partner_type,
      } = await getInvoicingBookReport(
        dispatch,
        start.toString(),
        end.toString(),
      );
      const responseWithId = completeInvoices.map(
        (item: any, index: number) => ({
          ...item,
          id: index,
        }),
      );

      const certificateResponse = await getSelectedCertificate(dispatch);
      setSelectedCertificate(certificateResponse);
      setData(responseWithId);
      setAlreadyInvoicedInvoices(alreadyInvoicedInvoices);
      setInvoicingPartner(system_invoicing_partner_type);
    } catch (error) {
      console.log(error);
      alert('Error al obtener los datos');
    }
    setLoading(false);
  };

  const checkDocuemntStatus = async (id: any) => {
    try {
      const row = alreadyInvoicedInvoices.find((item: any) => item.id === id);
      if (!row) return;
      const invoices = [
        {
          businessIdentifier: row.businessIdentifier,
          docType: row.docType,
          folio: row.folio,
        },
      ];
      const response = await getDocumentStatus(dispatch, { invoices });
      const changedInvoice = response?.filter(
        (invoice: any) => invoice.statusChanged,
      );
      if (changedInvoice && changedInvoice.length > 0) {
        await setUp();
      }
      setDteStatusDialogOpen(true);
      setDteStatus(response);
      return response;
    } catch (error) {
      return 'Error al obtener el estado del documento';
    }
  };

  const handleRefillInvoicingData = async () => {
    const selectedInvoices = data.filter((item: any) =>
      selectedIds.includes(item.id),
    );
    const selectedInvoicesWithMissingInfo = selectedInvoices.filter(
      (invoice: any) => {
        if (Array.isArray(invoice.missingInfo))
          return invoice.missingInfo.length > 0;
        if (invoice.missingInfo) {
          return Object.keys(invoice.missingInfo).length > 0;
        }
        return false;
      },
    );
    const identifiers = Array.from(
      new Set(
        selectedInvoicesWithMissingInfo
          .map((invoice: any) => invoice.businessIdentifier)
          .filter(Boolean),
      ),
    ) as string[];

    if (!selectedInvoices.length) {
      showAlert(dispatch, 'warning', 'Selecciona al menos un cliente.');
      return;
    }

    if (!identifiers.length) {
      showAlert(
        dispatch,
        'warning',
        'Los clientes seleccionados no tienen datos faltantes para refill.',
      );
      return;
    }

    try {
      setRefillLoading(true);
      const response = await refillInvoicingInformation(dispatch, {
        identifiers,
      });

      const manualRequired = response?.manualRequired || [];
      const siiErrors = response?.siiErrors || [];
      const withoutActiveBusinessRelation =
        response?.withoutActiveBusinessRelation || [];
      const updated = response?.updated || 0;
      const created = response?.created || 0;
      const skipped = response?.skipped || 0;

      if (manualRequired.length > 0 || siiErrors.length > 0) {
        const warningParts = [
          `Refill ejecutado con observaciones. Actualizados: ${updated}, creados: ${created}, sin cambios: ${skipped}.`,
        ];
        if (siiErrors.length > 0) {
          warningParts.push(`Errores SII: ${siiErrors.length}.`);
        }
        if (withoutActiveBusinessRelation.length > 0) {
          warningParts.push(
            `Ruts no activos: ${withoutActiveBusinessRelation.join(',')}.`,
          );
        }
        showAlert(dispatch, 'warning', warningParts.join(' '));
      } else {
        showAlert(
          dispatch,
          'success',
          `Refill ejecutado. Actualizados: ${updated}, creados: ${created}, sin cambios: ${skipped}.`,
        );
      }

      await setUp();
    } catch (error) {
      showAlert(dispatch, 'error', 'No se pudo ejecutar el refill.');
    } finally {
      setRefillLoading(false);
    }
  };

  const handleCheckDoc = {
    key: 'status',
    label: '',
    handleAction: checkDocuemntStatus,
    icon: <Info />,
  };

  const parseComissionsToInvoices = (
    preInvoices: any,
    subjectOption?: 'common' | 'details' | 'perOperation',
    commonSubject?: string,
  ) => {
    const invoices = preInvoices.map((invoice: any, index: number) => {
      const dateYYYYMMDD = endDate.format('YYYY-MM-DD');

      let details = [];

      if (subjectOption === 'common') {
        const subject = commonSubject || 'Servicios Factoring';
        details = [
          {
            subject: subject,
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      } else if (subjectOption === 'details' && invoice.allBusinessCosts) {
        const subject = 'Comisiones: ';
        const subjectText =
          subject +
          invoice.allBusinessCosts
            .map((cost: any) => `${cost.requestType} ${cost.requestId}`)
            .join(', ');
        details = [
          {
            subject: subjectText,
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      } else if (subjectOption === 'perOperation' && invoice.allBusinessCosts) {
        // Crear un detalle por cada operación
        details = invoice.allBusinessCosts.map((cost: any) => ({
          subject: `${cost.requestType} ${cost.requestId}`,
          qty: 1,
          amount: cost.operationCostTotal + cost.operationExpensesTotal,
        }));
      } else {
        details = [
          {
            subject: 'Servicios Factoring',
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      }

      return {
        index: index + 1,
        identifier: invoice.businessIdentifier,
        name: invoice.businessName,
        issuedDate: dateYYYYMMDD,
        amount: invoice.amountToInvoiceByPruff,
        amountWithIva: invoice.totalCostsWithIva,
        commune: invoice.commune,
        address: invoice.address,
        email: invoice.email,
        details: details,
      };
    });
    return invoices;
  };

  const openConfirmDialog = () => {
    if (!selectedCertificate) {
      alert(
        'No tienes un certificado seleccionado. Para utilizar esta función, anda a configuración y selecciona un certificado.',
      );
      return;
    }
    setSubjectDialogOpen(true);
  };

  const handleSubjectDialogConfirm = (
    option: 'common' | 'details' | 'perOperation',
    commonSubject?: string,
  ) => {
    const selectedInvoices = data.filter((item: any) =>
      selectedIds.includes(item.id),
    );
    const formattedInvoices = parseComissionsToInvoices(
      selectedInvoices,
      option,
      commonSubject,
    );
    setSelectedIds([]);
    setSelected(formattedInvoices);
    setOpenInvoiceDialog(true);
  };

  // Nullment functions
  const openNullmentGlossDialog = () => {
    if (!selectedCertificate) {
      alert(
        'No tienes un certificado seleccionado. Para utilizar esta función, anda a configuración y selecciona un certificado.',
      );
      return;
    }

    // Obtener las facturas seleccionadas (la tabla ya garantiza que solo se pueden seleccionar "Aceptadas")
    const selectedInvoices = alreadyInvoicedInvoices.filter((invoice: any) =>
      selectedNullmentIds.includes(invoice.id),
    );

    if (selectedInvoices.length === 0) {
      alert('No hay facturas seleccionadas.');
      return;
    }

    setSelectedNullmentInvoices(selectedInvoices);
    setNullmentGlossDialogOpen(true);
  };

  const parseInvoicesToNullments = (invoices: any[], gloss: string) => {
    // Fecha por defecto: último día del mes seleccionado en el MonthYearSearcher
    const defaultDate = endDate.format('YYYY-MM-DD');

    return invoices.map((invoice: any, index: number) => ({
      index: index + 1,
      folio: invoice.folio,
      docType: invoice.docType,
      businessIdentifier: invoice.businessIdentifier,
      stakeholderIdentifier: invoice.stakeholderIdentifier,
      stakeholderName: invoice.stakeholderName,
      amount: invoice.amount,
      originalAmount: invoice.amount, // Guardar el monto original para validaciones
      nullmentType: 1, // Por defecto es completa (1)
      gloss: gloss,
      issuedDate: defaultDate, // Fecha por defecto: último día del mes seleccionado
    }));
  };

  const handleNullmentGlossConfirm = (gloss: string) => {
    setNullmentGloss(gloss);
    const formattedNullments = parseInvoicesToNullments(
      selectedNullmentInvoices,
      gloss,
    );
    setSelectedNullmentInvoices(formattedNullments);
    setNullmentGlossDialogOpen(false);
    setNullmentConfirmDialogOpen(true);
  };

  useEffect(() => {
    setUp();
  }, []);

  const customAlertComponent = (missingInfo: any) => {
    if (!missingInfo) return null;

    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose emitidas a la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'key', label: 'Campo' },
            { key: 'value', label: 'Valor' },
          ]}
          data={missingInfo}
          rowsPerPageDefault={100}
          lastElement
        />
      </div>
    );
    return (
      <LightTooltip title={customTitle} placement="right">
        <Circle color="warning" />
      </LightTooltip>
    );
  };

  const customInvoicingComponent = (
    emittedInvoices: any,
    type: 'SII' | 'Pruff',
  ) => {
    if (!emittedInvoices) return null;
    const amountKey = type === 'SII' ? 'netAmount' : 'amount';
    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose emitidas a la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'folio', label: 'Folio' },
            { key: 'code', label: 'Tipo Doc' },
            { key: amountKey, label: 'Monto', type: 'money' },
            { key: 'issuedDate', label: 'Emisión', type: 'date' },
            { key: 'status', label: 'Estado' },
          ]}
          data={emittedInvoices}
          rowsPerPageDefault={100}
          lastElement
        />
      </div>
    );
    const totalEmittedGross = emittedInvoices.reduce(
      (acc: number, invoice: any) => acc + invoice[amountKey],
      0,
    );
    const totalEmittedNet = emittedInvoices
      .filter((invoice: any) => invoice.status === 'Aceptada')
      .reduce((acc: number, invoice: any) => acc + invoice[amountKey], 0);
    const emittedAlert = totalEmittedGross - totalEmittedNet > 0 ? true : false;
    return (
      <LightTooltip title={customTitle} placement="right">
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <Typography variant="body1">
            {formatMoney(totalEmittedNet)} {emittedAlert && '*'}
          </Typography>
        </div>
      </LightTooltip>
    );
  };

  const customColumnComponent = (allBusinessCosts: any) => {
    if (!allBusinessCosts) return null;
    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose comisiones de la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'requestId', label: 'Id' },
            { key: 'requestType', label: 'Tipo' },
            {
              key: 'operationCostTotal',
              label: 'Costo operación',
              type: 'money',
            },
            {
              key: 'operationCostTotalIva',
              label: 'IVA Costo',
              type: 'money',
            },
            {
              key: 'operationCostTotalWithIva',
              label: 'Total Costo',
              type: 'money',
            },
            {
              key: 'operationExpensesTotal',
              label: 'Gastos Solicitud',
              type: 'money',
            },
            {
              key: 'operationExpensesTotalIva',
              label: 'IVA Gastos',
              type: 'money',
            },
            {
              key: 'operationExpensesTotalWithIva',
              label: 'Total Gastos',
              type: 'money',
            },
            {
              key: 'operationDate',
              label: 'Contabilizado en',
              type: 'date',
            },
            {
              key: 'financingAt',
              label: 'Financiado en',
              type: 'date',
            },
            handleCheckDoc,
          ]}
          data={allBusinessCosts}
          rowsPerPageDefault={100}
          lastElement
          finalSumRow
        />
      </div>
    );
    return (
      <LightTooltip title={customTitle} placement="right">
        <div style={{ cursor: 'pointer' }}>
          <Info />
        </div>
      </LightTooltip>
    );
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        customTitle={
          <MonthYearSearcher
            year={year}
            setYear={setYear}
            month={month}
            setMonth={setMonth}
            setUp={setUp}
          />
        }
        variant="body1"
        buttons={
          type === 'Por facturar'
            ? [
                {
                  action: () => openConfirmDialog(),
                  name: 'Facturar',
                  color: 'primary',
                  disabled: selectedIds.length === 0,
                  startIcon: <Receipt />,
                },
                {
                  action: () => exportToExcel(),
                  name: 'Descargar XSLX',
                  color: 'primary',
                  disabled: selectedIds.length === 0,
                  startIcon: <Download />,
                },
                {
                  action: () => handleRefillInvoicingData(),
                  name: refillLoading ? 'Sincronizando...' : 'Datos SII',
                  color: 'secondary',
                  disabled: selectedIds.length === 0 || refillLoading,
                  startIcon: <Autorenew />,
                },
              ]
            : [
                {
                  action: () => openNullmentGlossDialog(),
                  name: 'Anular',
                  color: 'error',
                  disabled: selectedNullmentIds.length === 0,
                  startIcon: <Block />,
                },
              ]
        }
      />
      <Box padding="10px 0px">
        <ToggleButtonGroup
          value={type}
          color="primary"
          exclusive
          onChange={(_, newValue) => {
            setType(newValue);
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '140px',
            },
          }}
        >
          <ToggleButton value="Por facturar">Por facturar</ToggleButton>
          <ToggleButton value="Facturado">Facturado</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {type === 'Por facturar' && (
        <EnhancedTable
          dataHeaders={[
            ...(invoicingPartner === 'cessionDTE'
              ? [
                  {
                    key: 'missingInfo',
                    label: 'Datos',
                    customComponent: customAlertComponent,
                    type: 'custom',
                  },
                ]
              : []),
            {
              key: 'allBusinessCosts',
              label: 'Desglose',
              customComponent: customColumnComponent,
              type: 'custom',
            },
            {
              key: 'emittedInvoicesSII',
              label: 'Emitido SII',
              customComponent: (emittedInvoices: any) =>
                customInvoicingComponent(emittedInvoices, 'SII'),
              type: 'custom',
            },
            {
              key: 'emittedInvoicesPruff',
              label: 'Emitido Pruff',
              customComponent: (emittedInvoices: any) =>
                customInvoicingComponent(emittedInvoices, 'Pruff'),
            },
            { key: 'businessIdentifier', label: 'RUT cliente' },
            { key: 'businessName', label: 'Nombre cliente' },
            { key: 'totalQuoteCost', label: 'Factoring', type: 'money' },
            { key: 'totalCreditCosts', label: 'Crédito', type: 'money' },
            { key: 'totalConfirmingCosts', label: 'Confirming', type: 'money' },
            { key: 'totalExtensionsCosts', label: 'Prórroga', type: 'money' },
            { key: 'totalCosts', label: 'Total neto', type: 'money' },
            { key: 'totalCostsIva', label: 'Total Iva', type: 'money' },
            {
              key: 'totalCostsWithIva',
              label: 'Total factura',
              type: 'money',
            },
            {
              key: 'amountToInvoiceByPruff',
              label: 'Monto a facturar',
              type: 'money',
            },
          ]}
          data={data}
          actions={[]}
          showActions
          rowsPerPageDefault={100}
          selected={selectedIds}
          setSelected={setSelectedIds}
          exportToFile="xlsx"
          canSelectAll
          finalSumRow={true}
        />
      )}
      {type === 'Facturado' && (
        <EnhancedTable
          data={alreadyInvoicedInvoices}
          dataHeaders={[
            {
              key: 'folio',
              label: 'Folio',
            },
            {
              key: 'docType',
              label: 'Tipo doc.',
            },
            { key: 'stakeholderName', label: 'Nombre deudor' },
            { key: 'stakeholderIdentifier', label: 'RUT deudor' },
            { key: 'amount', label: 'Monto', type: 'money' },
            { key: 'issuedDate', label: 'Emisión', type: 'date' },
            ...(invoicingPartner === 'cessionDTE' ? [handleCheckDoc] : []),
          ]}
          rowsPerPageDefault={100}
          exportToFile="xlsx"
          lastElement
          selected={selectedNullmentIds}
          setSelected={setSelectedNullmentIds}
          showActions
          actions={[]}
          cantSelectRow={(row: any) =>
            row.status?.toLowerCase() !== 'aceptada' ||
            row.docType?.toLowerCase() === '61'
          }
        />
      )}

      <ReportInvoicingConfirmDialog
        confirmOpen={openInvoiceDialog}
        setConfirmOpen={setOpenInvoiceDialog}
        selectedInvoices={selected}
        setSelectedInvoices={setSelected}
        startDate={startDate}
        setUp={setUp}
        endDate={endDate}
        setSuccessInvoices={setSuccessInvoices}
        setResultInvoices={setResultInvoices}
        selectedCertificate={selectedCertificate}
        setFinalizedModal={setFinalizedModal}
      />
      {finalizedModal && (
        <TheBillerFinalizedModal
          finalizedModal={finalizedModal}
          setFinalizedModal={setFinalizedModal}
          resultInvoices={resultInvoices}
          successInvoices={successInvoices}
          handleClose={handleClose}
        />
      )}
      <SubjectOptionsDialog
        open={subjectDialogOpen}
        onClose={() => setSubjectDialogOpen(false)}
        onConfirm={handleSubjectDialogConfirm}
        currentSubject="Servicios Factoring"
      />
      <DteStatusDialog
        open={dteStatusDialogOpen}
        onClose={() => setDteStatusDialogOpen(false)}
        data={dteStatus}
      />
      <NullmentGlossDialog
        open={nullmentGlossDialogOpen}
        onClose={() => setNullmentGlossDialogOpen(false)}
        onConfirm={handleNullmentGlossConfirm}
        selectedCount={selectedNullmentInvoices.length}
      />
      <NullmentConfirmDialog
        confirmOpen={nullmentConfirmDialogOpen}
        setConfirmOpen={setNullmentConfirmDialogOpen}
        selectedInvoices={selectedNullmentInvoices}
        setSelectedInvoices={(invoices: any) => {
          setSelectedNullmentInvoices(invoices);
          if (invoices.length === 0) {
            setSelectedNullmentIds([]);
          }
        }}
        selectedCertificate={selectedCertificate}
        setUp={setUp}
        setFinalizedModal={setFinalizedModal}
        setSuccessInvoices={setSuccessInvoices}
        setResultInvoices={setResultInvoices}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export default InvoicingBook;
